/**
 * Employee Permission Constants
 *
 * Business rules for employee permissions and deletability.
 * These constants define which permissions and roles have special handling.
 *
 * @module personnel/domain/constants/permissions
 */

/**
 * Permissions that indicate an "install" user (POS/Terminal access)
 * Users with these permissions have special deletion restrictions
 */
export const INSTALL_PERMISSIONS = ['pos.loginAccess', 'payTerminal.loginAccess'] as const;

/**
 * Type for install permissions
 */
export type InstallPermission = typeof INSTALL_PERMISSIONS[number];

/**
 * Role names that cannot be deleted
 * These are system-critical roles
 */
export const UNDELETABLE_ROLES = ['Super Admin'] as const;

/**
 * Type for undeletable roles
 */
export type UndeletableRole = typeof UNDELETABLE_ROLES[number];

/**
 * Check if a permission is an install permission
 */
export const isInstallPermission = (permission: string): permission is InstallPermission => {
  return INSTALL_PERMISSIONS.includes(permission as InstallPermission);
};

/**
 * Check if a role is undeletable
 */
export const isUndeletableRole = (roleName: string): roleName is UndeletableRole => {
  return UNDELETABLE_ROLES.includes(roleName as UndeletableRole);
};
